import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface DatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (date: string) => void;
  label?: string;
}

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function DatePickerDialog({ open, onClose, value, onChange, label = 'Selecionar data' }: DatePickerDialogProps) {
  const today = format(new Date(), 'yyyy-MM-dd');

  const [currentMonth, setCurrentMonth] = useState(() =>
    value ? value.substring(0, 7) : today.substring(0, 7),
  );

  useEffect(() => {
    if (open) setCurrentMonth(value ? value.substring(0, 7) : today.substring(0, 7));
  }, [open, value, today]);

  const monthStart = startOfMonth(parseISO(`${currentMonth}-01`));
  const monthEnd = endOfMonth(monthStart);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const blanks = Array<null>(getDay(monthStart)).fill(null);

  function prevMonth() {
    setCurrentMonth(format(subMonths(monthStart, 1), 'yyyy-MM'));
  }
  function nextMonth() {
    setCurrentMonth(format(addMonths(monthStart, 1), 'yyyy-MM'));
  }

  const monthLabel = format(monthStart, 'MMMM yyyy', { locale: ptBR });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent showCloseButton={false} className="max-w-xs p-0 gap-0">
        <div className="p-4">
          <DialogTitle className="sr-only">{label}</DialogTitle>

          <div className="mb-3 flex items-center justify-between">
            <button
              onClick={prevMonth}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Mês anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold capitalize text-slate-900">{monthLabel}</span>
            <button
              onClick={nextMonth}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
              aria-label="Próximo mês"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7">
            {WEEK_DAYS.map((d) => (
              <span key={d} className="py-1 text-center text-[10px] font-medium text-slate-400">
                {d}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {blanks.map((_, i) => <div key={`b${i}`} />)}
            {days.map((day) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isSelected = dateStr === value;
              const isToday = dateStr === today;
              return (
                <button
                  key={dateStr}
                  onClick={() => { onChange(dateStr); onClose(); }}
                  className={cn(
                    'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                    isSelected
                      ? 'bg-slate-900 font-semibold text-white'
                      : isToday
                        ? 'ring-1 ring-slate-300 text-slate-900 hover:bg-slate-100'
                        : 'text-slate-700 hover:bg-slate-100',
                  )}
                  aria-pressed={isSelected}
                  aria-label={format(day, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
